import useOverlay from 'hooks/use-overlay';
import { useQueryClient } from '@tanstack/react-query';
import { PAGES, ReactQueryKeys, SharePointTitle, UNAUTHORIZED_PAGES } from 'common/enums';
import { requestService } from 'services/requests-service/requests-service';
import {
  AcceptIncidentOptional,
  RejectionCode,
  PostponeIncidentKeys,
  StepType,
  UpdateStepKeys,
  UploadSettleUpDateType
} from 'services/requests-service/requests-service.props';
import { FormName, IFormData } from 'pages/my-requests/components/table/expand/date-form/date-form.props';
import {
  StatusDescriptionId,
  VehicleReadinessConfirmedId
} from 'pages/my-requests/components/table/expand/expand.props';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { setCompletedIncidentId, setIsEditMode, setUpdateIncidentId } from 'actions/pages/my-requests.action';
import { Steps } from 'pages/my-requests/components/table/expand/steps-controller/steps.props';
import { useContext, useState } from 'react';
import {
  getAdditionalFilesForSubmit,
  getSharePointFilesForSubmit
} from 'pages/my-requests/components/table/expand/steps-controller/completion-confirmed/form/utils';
import { AnyObject, ResponseModel } from 'common/interfaces';
import useRequestSnackbar from 'hooks/use-request-snackbar';
import { AxiosResponse } from 'axios';
import { RequestStatuses } from '../../../constants';
import { useIncidentByIdQuery } from 'hooks/react-query/incidents/incident-by-id';
import { UnSaveModalMyRequestRefContext } from 'context/un-save-modal/my-request-ref.context';
import { useMyRequestsFilers } from 'hooks/react-query/incidents/my-requests/my-requests-filters';

import { useUploadIncidentFiles } from './use-upload-incident-files';
import { ExpandContext } from '@/pages/my-requests/components/table/expand/utils';

export const useSendRequest = (incidentIdProp: string | null) => {
  const { showRequestSnackbar } = useRequestSnackbar();

  const dispatch = useDispatch();

  const [showOverlay, hideOverlay] = useOverlay();
  const queryClient = useQueryClient();

  const { activeStep, activeIncident } = useContext(UnSaveModalMyRequestRefContext);
  const { setIsIncidentSubmiting, setShowValidationScreen, setIsIncidentSubmitingError } = useContext(ExpandContext);

  const { refetch } = useIncidentByIdQuery((activeIncident.current.incidentid || incidentIdProp) as string, {
    enabled: false
  });

  const { uploadError, uploadIncidentFiles } = useUploadIncidentFiles();

  const { refetch: filtersRefetch } = useMyRequestsFilers();

  const clearSharePointCash = () => {
    queryClient.resetQueries({ queryKey: [ReactQueryKeys.SharePoint, ReactQueryKeys.RetrieveRepairPartnerPhotos] });
    queryClient.resetQueries({ queryKey: [ReactQueryKeys.SharePoint, ReactQueryKeys.RetrieveIncidentDetailedFiles] });
  };

  const clearCache = () => {
    refetch();

    queryClient.resetQueries({ queryKey: [PAGES.REPAIRED] });
    queryClient.resetQueries({ queryKey: [UNAUTHORIZED_PAGES.REPAIRED_STATUS] });
  };

  const finallyCallback = () => {
    clearCache();
    dispatch(setIsEditMode(false));
  };

  const convertDataToUploadSettleUpDate = (values: AnyObject) => {
    const data: Partial<UploadSettleUpDateType> = {};

    Object.entries(values).map(([key, value]) => {
      if (moment(value).isValid()) {
        // @ts-ignore
        data[key] = moment(value).format('YYYY-MM-DD');
      }
    });

    return data;
  };

  const sendEmailOnLoadFileHandler = (files: Record<SharePointTitle, File[]>, isCompletionConfirmedStep: boolean) => {
    const incidentId = incidentIdProp || (activeIncident.current.incidentid as string);

    if (isCompletionConfirmedStep) {
      if (Object.values(files).flat().length) {
        requestService.sendEmailOnLoadFile(
          incidentId,
          5,
          '',
          Object.values(files)
            .flat()
            .map(fileItem => fileItem.name)
        );
      }
    }
  };

  const saveFiles = async (
    stepType: StepType,
    files: Record<SharePointTitle, File[]>,
    resetFiles: Function,
    values: AnyObject,
    isCompletionConfirmedStep = false,
    settleUpDate?: UploadSettleUpDateType,
    carReadyToUseCode?: number
  ) => {
    const incidentId = incidentIdProp || (activeIncident.current.incidentid as string);
    let response: AxiosResponse<ResponseModel> | null = null;

    if (stepType !== 5) {
      showOverlay();
    } else {
      setIsIncidentSubmiting(true);
      setShowValidationScreen(true);
    }

    try {
      if (hasFiles(files)) {
        const uploadResult = await uploadIncidentFiles({
          data: files,
          incidentId
        });

        if (uploadResult.isError) {
          setIsIncidentSubmitingError(true);
          return;
        }
      }

      if (isCompletionConfirmedStep) {
        response = await requestService.uploadSettleUpDate(
          incidentId,
          5,
          convertDataToUploadSettleUpDate(settleUpDate as AnyObject) as UploadSettleUpDateType
        );

        setIsIncidentSubmitingError(response?.data?.level !== RequestStatuses.Success);
      } else {
        const convertRepairExtensionValue = () => {
          if (typeof values[UpdateStepKeys.RepairExtension] === 'boolean') {
            return values[UpdateStepKeys.RepairExtension] ? 752560001 : 752560000;
          }
        };

        response = await requestService.updateStep({
          [UpdateStepKeys.IncidentId]: incidentId,
          [UpdateStepKeys.StepDate]: new Date().toISOString(),
          [UpdateStepKeys.StepType]: stepType,
          [UpdateStepKeys.CarReadyToUse]: carReadyToUseCode,
          [UpdateStepKeys.RepairExtension]: convertRepairExtensionValue()
        });

        showRequestSnackbar({
          successCondition: response?.data?.level === RequestStatuses.Success,
          successCallback: () => {
            finallyCallback();
            resetFiles();
            clearSharePointCash();
          }
        });
      }

      sendEmailOnLoadFileHandler(files, isCompletionConfirmedStep);
      dispatch(setUpdateIncidentId(incidentId));
    } catch (err) {
      showRequestSnackbar({
        duration: 5_000,
        successCondition: false,
        errorMessage: (err as Error).message,
        errorCallback: () => {
          resetFiles();
          clearSharePointCash();
        }
      });
    } finally {
      hideOverlay();
      setIsIncidentSubmiting(false);
    }
  };

  const updateStep = async (
    values: IFormData,
    stepType: StepType,
    vehicleReadinessConfirmed?: VehicleReadinessConfirmedId,
    requestReasonName: UpdateStepKeys = UpdateStepKeys.DateConfirmation
  ) => {
    const incidentId = incidentIdProp || (activeIncident.current.incidentid as string);
    let mainResponse: AxiosResponse<ResponseModel> | null = null;
    let secondaryResponse: AxiosResponse<ResponseModel> | null = null;

    try {
      showOverlay();

      if (values[FormName.DelayedIncidentCheckBox]) {
        if (!values[FormName.ModalRadioValue]) {
          mainResponse = await requestService.postponeIncident({
            [PostponeIncidentKeys.Id]: incidentId,
            [PostponeIncidentKeys.IsDeclined]: false,
            [PostponeIncidentKeys.Date]: moment(values[FormName.DelayedIncidentDate]).format('YYYY-MM-DD'),
            [PostponeIncidentKeys.Reason]: values[FormName.Reason]
          });
          queryClient.resetQueries({ queryKey: [PAGES.MY_REQUESTS] });
        }
        if (
          values[FormName.OldDate]?.isBefore(values[FormName.DelayedIncidentDate]) &&
          values[FormName.DifferenceDate] &&
          values[FormName.ModalRadioValue] === 'option1'
        ) {
          mainResponse = await requestService.postponeIncident({
            [PostponeIncidentKeys.Id]: incidentId,
            [PostponeIncidentKeys.IsDeclined]: false,
            [PostponeIncidentKeys.Date]: moment(values[FormName.DelayedIncidentDate]).format('YYYY-MM-DD'),
            [PostponeIncidentKeys.Reason]: values[FormName.Reason]
          });
          queryClient.resetQueries({ queryKey: [PAGES.MY_REQUESTS] });

          if (mainResponse?.data?.level === RequestStatuses.Success) {
            secondaryResponse = await requestService.updateStep({
              [UpdateStepKeys.IncidentId]: incidentId,
              [UpdateStepKeys.StepDate]: moment(values[FormName.DifferenceDate]).format('YYYY-MM-DD'),
              [UpdateStepKeys.StepType]: stepType,
              [UpdateStepKeys.VehicleReadinessConfirmed]: vehicleReadinessConfirmed
            });
          }
        }
        if (
          values[FormName.OldDate]?.isBefore(values[FormName.DelayedIncidentDate]) &&
          values[FormName.ModalRadioValue] === 'option2'
        ) {
          mainResponse = await requestService.postponeIncident({
            [PostponeIncidentKeys.Id]: incidentId,
            [PostponeIncidentKeys.IsDeclined]: false,
            [PostponeIncidentKeys.Date]: moment(values[FormName.DelayedIncidentDate]).format('YYYY-MM-DD'),
            [PostponeIncidentKeys.Reason]: values[FormName.Reason]
          });
          queryClient.resetQueries({ queryKey: [PAGES.MY_REQUESTS] });

          if (mainResponse?.data?.level === RequestStatuses.Success) {
            secondaryResponse = await requestService.updateStep({
              [UpdateStepKeys.IncidentId]: incidentId,
              [UpdateStepKeys.StepDate]: moment(values[FormName.DelayedIncidentDate]).format('YYYY-MM-DD'),
              [UpdateStepKeys.StepType]: stepType,
              [UpdateStepKeys.VehicleReadinessConfirmed]: vehicleReadinessConfirmed
            });
          }
        }
      } else {
        mainResponse = await requestService.updateStep({
          [UpdateStepKeys.IncidentId]: incidentId,
          [UpdateStepKeys.StepDate]: moment(values[FormName.StepDate]).format('YYYY-MM-DD'),
          [requestReasonName]: values[FormName.StepDateJustification],
          [UpdateStepKeys.StepType]: stepType,
          [UpdateStepKeys.VehicleReadinessConfirmed]: vehicleReadinessConfirmed
        });
      }

      dispatch(setUpdateIncidentId(incidentId));
    } catch (err) {
      showRequestSnackbar({ successCondition: false, successCallback: () => console.error(err) });
    } finally {
      if (secondaryResponse && secondaryResponse.data.level !== RequestStatuses.Success) {
        showRequestSnackbar({ successCondition: false, successCallback: finallyCallback });
      } else {
        showRequestSnackbar({
          successCondition: mainResponse?.data.level === RequestStatuses.Success,
          successCallback: finallyCallback
        });
      }
    }
  };

  const uploadSettleUpDate = async (stepType: StepType, values: UploadSettleUpDateType) => {
    const incidentId = incidentIdProp || (activeIncident.current.incidentid as string);
    let response: AxiosResponse<ResponseModel> | null = null;

    try {
      showOverlay();

      response = await requestService.uploadSettleUpDate(
        incidentId,
        stepType,
        convertDataToUploadSettleUpDate(values) as UploadSettleUpDateType
      );

      filtersRefetch();
      dispatch(setUpdateIncidentId(incidentId));
    } catch (err) {
      showRequestSnackbar({ successCondition: false, successCallback: () => console.error(err) });
    } finally {
      showRequestSnackbar({
        successCondition: response?.data.level === RequestStatuses.Success,
        successCallback: finallyCallback
      });
    }
  };

  const declineIncident = async (rejectionCode: RejectionCode, stepType: StepType) => {
    const incidentId = incidentIdProp || (activeIncident.current.incidentid as string);
    let response: AxiosResponse<ResponseModel> | null = null;

    try {
      showOverlay();

      response = await requestService.declineIncident(incidentId, rejectionCode, stepType);
      dispatch(setUpdateIncidentId(incidentId));
    } catch (err) {
      showRequestSnackbar({ successCondition: false, successCallback: () => console.error(err) });
    } finally {
      showRequestSnackbar({
        successCondition: response?.data.level === RequestStatuses.Success,
        successCallback: finallyCallback
      });
    }
  };

  const acceptIncident = async (
    statusDescriptionId: StatusDescriptionId,
    optional: Partial<AcceptIncidentOptional> = {}
  ) => {
    const incidentId = incidentIdProp || (activeIncident.current.incidentid as string);
    let response: AxiosResponse<ResponseModel> | null = null;

    try {
      showOverlay();
      response = await requestService.acceptIncident(incidentId, statusDescriptionId, optional);

      queryClient.resetQueries({ queryKey: [PAGES.MY_REQUESTS] });
      dispatch(setUpdateIncidentId(incidentId));
    } catch (err) {
      showRequestSnackbar({ successCondition: false, successCallback: () => console.error(err) });
    } finally {
      showRequestSnackbar({
        successCondition: response?.data.level === RequestStatuses.Success,
        successCallback: finallyCallback
      });
    }
  };

  const postponeIncident = async (values: Partial<IFormData>, isDeclined = false) => {
    const incidentId = incidentIdProp || (activeIncident.current.incidentid as string);
    let response: AxiosResponse<ResponseModel> | null = null;

    try {
      showOverlay();
      response = await requestService.postponeIncident({
        [PostponeIncidentKeys.Id]: incidentId,
        [PostponeIncidentKeys.IsDeclined]: isDeclined,
        [PostponeIncidentKeys.Date]: moment(values[FormName.DelayedIncidentDate]).format('YYYY-MM-DD'),
        [PostponeIncidentKeys.Reason]: values[FormName.Reason]
      });

      queryClient.resetQueries({ queryKey: [PAGES.MY_REQUESTS] });
      dispatch(setUpdateIncidentId(incidentId));
    } catch (err) {
      showRequestSnackbar({ successCondition: false, successCallback: () => console.error(err) });
    } finally {
      showRequestSnackbar({
        successCondition: response?.data.level === RequestStatuses.Success,
        successCallback: finallyCallback
      });
    }
  };

  const switchProcessIncident = async (orderStatusDescriptionId: string) => {
    const incidentId = incidentIdProp || (activeIncident.current.incidentid as string);
    let response: AxiosResponse<ResponseModel> | null = null;

    try {
      showOverlay();
      response = await requestService.switchProcessIncident(incidentId, orderStatusDescriptionId);

      dispatch(setUpdateIncidentId(incidentId));
    } catch (err) {
      showRequestSnackbar({ successCondition: false, successCallback: () => console.error(err) });
    } finally {
      showRequestSnackbar({
        successCondition: response?.data.level === RequestStatuses.Success,
        successCallback: finallyCallback
      });
    }
  };

  const lastStepUploadedHandler = (incidentId: string) => {
    queryClient.resetQueries({ queryKey: [PAGES.MY_REQUESTS] });
    dispatch(setCompletedIncidentId(incidentId));
  };

  const sendRequestMyRequestPage = async (data: AnyObject) => {
    switch (activeStep.current) {
      case Steps.Second:
        return await updateStep(data as IFormData, activeIncident.current.isAbholungGeplant ? 2 : 1);
      case Steps.Third:
        return await saveFiles(4, data as Record<SharePointTitle, File[]>, () => {}, data);
      case Steps.Fourth:
        return await updateStep(data as IFormData, 2);
      case Steps.Fifth:
        return await updateStep(data as IFormData, 3);
      case Steps.Sixth:
        await saveFiles(
          5,
          {
            ...getAdditionalFilesForSubmit(data),
            ...getSharePointFilesForSubmit(data)
          } as Record<SharePointTitle, File[]>,
          () => {},
          {},
          true,
          data as UploadSettleUpDateType
        );
        lastStepUploadedHandler(activeIncident.current?.incidentid as string);
        break;
      default:
        break;
    }
  };

  return {
    uploadError,
    saveFiles,
    updateStep,
    uploadSettleUpDate,
    declineIncident,
    sendRequestMyRequestPage,
    acceptIncident,
    postponeIncident,
    switchProcessIncident,
    clearCache
  };
};

function hasFiles(files: Record<SharePointTitle, File[]>) {
  return Object.values(files).some(fileList => fileList.length > 0);
}
